import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { ChipModule } from 'primeng/chip';
import { DialogComponent } from "src/app/@core/components/ui/dialog/dialog.component";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from '../../../../../@core/components/ui/button/button.component';
import { MenuComponent, CustomMenuItem } from '../../../../../@core/components/ui/menu/menu.component';
import { IconComponent, IconSourceEnum } from '../../../../../@core/components/ui/icon/icon.component';
import { SelectComponent, SelectConfig, SelectOption } from '../../../../../@core/components/ui/select';
import { COMMENTS_TYPE, USER_TYPE } from '../../../../../@core/models/enums';
import { CONTRACT_USER_ROLE, DEACTIVATION_STATUS, IComment, IUser, SHARE_ACCESS, SHARE_FEATURE } from '../../../../../@core/models/model';
import { AuthenticationService } from '../../../../../@core/services/authentication.service';
import { ToastService } from '../../../../../@core/services/toast.service';
import { CommentsService } from '../../../../../contract-management/services/comments.service';
import { ShareContractService } from '../../../../../contract-management/services/share-contract.service';
import { UserService } from '../../../../../contract-management/services/user.service';
import { FullNamePipe } from '../../../../pipes/fullName.pipe';
import { UserAvatarPipe } from '../../../../pipes/user-avatar-pipe';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';

@Component({
  selector: 'app-comments-tab',
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    InputGroupModule,
    InputGroupAddonModule,
    FullNamePipe,
    UserAvatarPipe,
    TooltipModule,
    AvatarModule,
    ChipModule,
    DialogComponent,
    RadioButtonModule,
    SelectModule,
    Textarea,
    ButtonComponent,
    SelectComponent,
    MenuComponent,
    AssetUrlPipe,
    UserNameDisplayComponent,
    IconComponent
  ],
  standalone: true,
  templateUrl: './comments-tab.component.html'
})

export class CommentsTabComponent implements OnInit {

  @ViewChild('commentsContainer') commentsContainer!: ElementRef;
  @ViewChild('addCommentInput') addCommentInput!: ElementRef;
  @ViewChild('editCommentInput') editCommentInput!: ElementRef;
  @ViewChild('commentsTabContainer') commentsTabContainer!: ElementRef;

  private _itemId: string = '';
  @Input()
  set itemId(value: string) {
    this._itemId = value;
    this.getComments()
  }
  get itemId(): string {
    return this._itemId;
  }

  @Input() userRole: string = '';

  @Input() contractId: string = '';

  @Input() readonly = false;

  needPermissionDialog: boolean = false;
  needAccessUsers: IUser[] = [];
  permissionOptions: SelectOption[] = [
    { label: 'Commenter', value: 'Comment' },
    { label: 'Editor', value: 'Edit' },
    { label: 'Viewer', value: 'View' },
  ];

  permissionConfig: SelectConfig = {
    placeholder: 'Select permission',
    searchable: false,
    clearable: false,
    styleClass: 'border',
    appendTo: 'body'
  };

  selectedPermission: SHARE_ACCESS = SHARE_ACCESS.COMMENT;
  selectedAccessOption: string = 'shareWithPeople';
  @Input() commentsType: COMMENTS_TYPE = COMMENTS_TYPE.CONTRACT;

  canComment(): boolean {
    return this.userRole === CONTRACT_USER_ROLE.COMMENTER || this.userRole === CONTRACT_USER_ROLE.OWNER || this.userRole === CONTRACT_USER_ROLE.EDITOR;
  }

  addCommentText: string = '';
  DEACTIVATION_STATUS = DEACTIVATION_STATUS;
  comments: IComment[] = [];
  /**
   * Returns per-comment CustomMenuItem[] for app-menu.
   * Callbacks close over the specific comment so no label-switching is needed.
   */
  getCommentMenuItems(comment: IComment): CustomMenuItem[] {
    return [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        iconSource: IconSourceEnum.PRIMENG,
        callback: () => {
          this.editCommentId = comment._id;
          this.editCommentText = comment.comment;
        },
      },
      {
        label: 'Delete',
        icon: 'pi pi-trash',
        iconSource: IconSourceEnum.PRIMENG,
        class: 'text-red-500',
        callback: () => this.deleteComment(comment._id),
      },
    ];
  }
  currentUser!: IUser | null | undefined
  editCommentId: string = '';
  editCommentText: string = '';
  allUsers: IUser[] = [];
  filteredUsers: IUser[] = [];
  showMentionsDropdown: boolean = false;
  mentionsDropdownLeftPosition: number = 0;
  currentMentionQuery: string = '';
  selectedMentionIndex: number = 0;
  isInMentionMode: boolean = false;
  mentionsWidth: number = 256;//16rem

  constructor(
    private commentService: CommentsService,
    private authService: AuthenticationService,
    private toastService: ToastService,
    private userService: UserService,
    private shareUser: ShareContractService
  ) {
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res
    });
  }

  ngOnInit(): void {
    this.getComments();
    this.getUsers();
  }

  getUsers() {
    this.userService.getWorkspaceUsers().subscribe((res) => {
      if (res.success && res.data) {
        this.allUsers = res.data;
      }
    });
  }

  getComments() {
    this.comments = [];
    if (!this.itemId || this.itemId.trim() === '') {
      return;
    }
    this.commentService.getComments(this.itemId).subscribe((res) => {
      if (res.success && res.data) {
        this.comments = res.data;
      } else {
        this.comments = [];
      }
    })
  }

  addOrUpdateComment(text: string, mentions: string[]) {
    if (!this.itemId || this.itemId.trim() === '') {
      return;
    }
    this.commentService.addOrUpdateComment({
      comment: text,
      itemId: this.itemId,
      docModel: this.commentsType,
      mentions
    }).subscribe((res) => {
      if (res.success && res.data) {
        this.addCommentText = '';
        this.comments.push(res.data);
        this.needPermissionDialog = false;
        this.selectedAccessOption = 'shareWithPeople';
        this.selectedPermission = SHARE_ACCESS.COMMENT;
        this.needAccessUsers = [];
        setTimeout(() => {
          this.scrollToBottom();
        }, 0);
      }
    });
  }

  addComment() {
    if (this.addCommentText.trim() === '' || !this.itemId || this.itemId.trim() === '') {
      return;
    }
    const mentions = this.extractMentionsFromText(this.addCommentText);
    const text = this.getCommentTextWithoutMentions(this.addCommentText);
    if (this.commentsType === COMMENTS_TYPE.CONTRACT || this.commentsType === COMMENTS_TYPE.QUESTION) {
      this.commentService.checkPermissions(this.itemId, mentions, this.commentsType).subscribe((res) => {
        if (res.data && res.data.length > 0) {
          this.needAccessUsers = res.data as IUser[];
          if (this.needAccessUsers.length > 0) {
            this.initializeUserPermissions();
            this.needPermissionDialog = true;
          }
        }
        else {
          this.addOrUpdateComment(text, mentions);
        }
      })
    }
    else {
      this.addOrUpdateComment(text, mentions);
    }

  }

  scrollToBottom() {
    if (this.commentsContainer) {
      const element = this.commentsContainer.nativeElement;
      setTimeout(() => {
        element.scrollTop = element.scrollHeight;
      }, 50);
    }
  }

  handleMenuAction(action: string | undefined, comment: IComment) {
    if (!action) return;

    if (action === 'Delete') {
      this.deleteComment(comment._id);
    }
    else if (action === 'Edit') {
      this.editCommentId = comment._id;
      this.editCommentText = comment.comment;
    }
  }

  closeEditComment() {
    this.editCommentId = '';
    this.editCommentText = '';
  }

  editComment(comment: IComment) {
    const mentions = this.extractMentionsFromText(this.editCommentText);
    const text = this.getCommentTextWithoutMentions(this.editCommentText);

    this.commentService.addOrUpdateComment({
      _id: comment._id,
      comment: text,
      mentions: [...(mentions || []), ...((comment.mentions as IUser[]).map(user => user._id) || [])]
    }).subscribe((res) => {
      if (res.success && res.data) {
        this.toastService.success('Comment updated successfully');
        this.closeEditComment();
        this.comments = this.comments.map(comment => comment._id === res.data._id ? res.data : comment);
      }
    })
  }

  deleteComment(commentId: string) {
    this.commentService.deleteComment(commentId).subscribe((res) => {
      if (res.success) {
        this.toastService.success('Comment deleted successfully');
        this.comments = this.comments.filter(comment => comment._id !== commentId);
      }
    });
  }

  onCommentInput(event: any, isEdit: boolean = false) {
    const text = event.target.value;
    const cursorPosition = event.target.selectionStart;

    const beforeCursor = text.substring(0, cursorPosition);
    const mentionMatch = beforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      this.isInMentionMode = true;
      this.currentMentionQuery = mentionMatch[1];
      this.showMentionsDropdown = true;
      this.selectedMentionIndex = 0;

      // Filter users based on query
      this.filteredUsers = this.allUsers.filter(user => {
        const fullName = user.fullName || `${user.firstName} ${user.lastName}`.trim();
        return fullName.toLowerCase().includes(this.currentMentionQuery.toLowerCase()) ||
          user.firstName?.toLowerCase().includes(this.currentMentionQuery.toLowerCase()) ||
          user.lastName?.toLowerCase().includes(this.currentMentionQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(this.currentMentionQuery.toLowerCase());
      });

      // Position dropdown near cursor
      this.positionMentionsDropdown(event.target, cursorPosition);
    } else {
      this.isInMentionMode = false;
      this.showMentionsDropdown = false;
    }
  }

  positionMentionsDropdown(inputElement: HTMLInputElement, cursorPosition: number) {
    const rect = inputElement.getBoundingClientRect();
    const inputWidth = rect.width;
    const textBeforeCursor = inputElement.value.substring(0, cursorPosition);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
      context.font = window.getComputedStyle(inputElement).font;
      const textWidth = context.measureText(textBeforeCursor).width;
      this.mentionsDropdownLeftPosition = textWidth + this.mentionsWidth > inputWidth ? inputWidth - this.mentionsWidth : textWidth;
    } else {
      this.mentionsDropdownLeftPosition = 0
    }
  }

  selectMention(user: IUser, isEdit: boolean = false) {
    const inputElement = isEdit ? this.editCommentInput.nativeElement : this.addCommentInput.nativeElement;
    const text = inputElement.value;
    const cursorPosition = inputElement.selectionStart;

    const beforeCursor = text.substring(0, cursorPosition);
    const afterCursor = text.substring(cursorPosition);
    const mentionMatch = beforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      const fullName = user.fullName || `${user.firstName} ${user.lastName}`.trim();
      const newText = beforeCursor.replace(/@(\w*)$/, `@${fullName} `) + afterCursor;

      if (isEdit) {
        this.editCommentText = newText;
      } else {
        this.addCommentText = newText;
      }

      const mentionStartIndex = beforeCursor.lastIndexOf('@');
      const newCursorPosition = mentionStartIndex + `@${fullName} `.length;

      requestAnimationFrame(() => {
        inputElement.focus();
        inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    }

    this.showMentionsDropdown = false;
    this.isInMentionMode = false;
  }

  onKeyDown(event: KeyboardEvent, isEdit: boolean = false) {
    if (this.showMentionsDropdown) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, this.filteredUsers.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
      } else if (event.key === 'Enter' && this.filteredUsers.length > 0) {
        event.preventDefault();
        this.selectMention(this.filteredUsers[this.selectedMentionIndex], isEdit);
      } else if (event.key === 'Escape') {
        this.showMentionsDropdown = false;
        this.isInMentionMode = false;
      }
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (isEdit) {
        const comment = this.comments.find(c => c._id === this.editCommentId);
        if (comment) {
          this.editComment(comment);
        }
      } else {
        this.addComment();
      }
    }
  }

  // Extract mentions by matching exact '@' + fullName occurrences
  extractMentionsFromText(text: string): string[] {
    if (!text || !this.allUsers?.length) return [];

    const mentionedUserIds: string[] = [];

    for (const user of this.allUsers) {
      const fullName = (user.fullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()).trim();
      if (!fullName) continue;
      const needle = `@${fullName}`;
      if (text.includes(needle)) {
        mentionedUserIds.push(user._id);
      }
    }

    return Array.from(new Set(mentionedUserIds));
  }

  // Remove exact '@' + fullName tokens from the text (leave the rest intact)
  getCommentTextWithoutMentions(text: string): string {
    if (!text || !this.allUsers?.length) return (text || '').trim();

    let result = text;
    for (const user of this.allUsers) {
      const fullName = (user.fullName || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()).trim();
      if (!fullName) continue;
      const needle = `@${fullName}`;
      result = result.split(needle).join('');
    }

    return result.replace(/\s+/g, ' ').trim();
  }

  hasMentions(comment: IComment): boolean {
    return !!(comment.mentions && Array.isArray(comment.mentions) && comment.mentions.length > 0);
  }

  getMentionsAsUsers(comment: IComment): IUser[] {
    if (!comment.mentions || !Array.isArray(comment.mentions)) {
      return [];
    }

    return comment.mentions.map(mention => {
      if (typeof mention === 'string') {
        const user = this.allUsers.find(user => user._id === mention);
        return user;
      } else {
        return mention as IUser;
      }
    }).filter(user => user !== undefined && user !== null) as IUser[];
  }

  initializeUserPermissions() {
    this.selectedAccessOption = 'shareWithPeople';
  }

  onAccessOptionChange(option: string) {
    this.selectedAccessOption = option;
  }


  proceedWithComment() {
    let mentions = this.extractMentionsFromText(this.addCommentText);
    let text = this.getCommentTextWithoutMentions(this.addCommentText);

    if (this.selectedAccessOption === 'shareWithPeople') {
      for (const user of this.needAccessUsers) {
        this.shareUser.addUserToShare(this.contractId, {
          email: user.email,
          role: this.selectedPermission,
          name: user.fullName,
          user: user,
          type: USER_TYPE.INTERNAL,
        }, SHARE_FEATURE.CONTRACT).subscribe((res) => {
          if (res.success) {
            this.toastService.success('Shared to user successfully');
          }
        })
      }
    } else if (this.selectedAccessOption === 'dontShare') {
      mentions = mentions.filter(mention => !this.needAccessUsers.some(user => user._id === mention));
      text += `\n\n${this.needAccessUsers.map(user => user.fullName ? user.fullName : `${user.firstName} ${user.lastName}`).join(', ')}`;
    }

    this.commentService.addOrUpdateComment({
      comment: text,
      itemId: this.itemId,
      docModel: this.commentsType,
      mentions
    }).subscribe((res) => {
      if (res.success && res.data) {
        this.addCommentText = '';
        this.comments.push(res.data);
        this.needPermissionDialog = false;
        this.selectedAccessOption = 'shareWithPeople';
        this.selectedPermission = SHARE_ACCESS.COMMENT;
        this.needAccessUsers = [];
        setTimeout(() => {
          this.scrollToBottom();
        }, 0);
      }
    });
  }

}

